/* eslint-disable sonarjs/cognitive-complexity */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEqual } from 'lodash';
import InputNumber from 'lib/InputNumber';
import ToggleSwitch from 'lib/ToggleSwitch';
import { changeDealCurrentLineData } from 'store/dealManagement/reducer';
import { getDealType } from 'store/dealManagement/selectors/getDealType';
import { Store } from 'components/common/types/Store.types';
import InfoMessage from 'components/common/Deal/InfoMessage';
import { INFO_TYPE } from 'components/common/Deal/InfoMessage/InfoMessage.types';
import { TooltipDirection } from 'lib/Tooltip';
import { DealType, DealStatus } from 'components/common/types/Deal.types';
import { CardTheme } from 'components/common/Card/Card.types';
import { sotType } from './ShareOfTime.types';
import { useSot } from './useSot';

type ShareOfTimeProps = {
  isDisabled: boolean;
};

const ShareOfTime = ({ isDisabled }: ShareOfTimeProps): React.ReactElement => {
  const dispatch = useDispatch();

  const [randomFloorKey, setRandomFloorKey] = useState(Math.random());
  const [randomCeilingKey, setRandomCeilingKey] = useState(Math.random());

  const dealType = useSelector(getDealType);
  const bookingStatusCode = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);
  const impressions = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.impressions);
  const budget = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.budget);
  const isDealTypeGuaranteed = dealType === DealType.GUARANTEED;

  const { sot, floorSot, ceilingSot, isToggled, isValid, getValues, setSot, handleToggle } = useSot();

  const isInputDisabled = isDisabled && bookingStatusCode !== DealStatus.PENDING_RESERVATION;
  const hasRange = !!(ceilingSot || floorSot || isToggled);
  const labelStyling = 'sub-header-base text-neutral-950-opacity-60 mb-2 inline-block';
  const invalidImpressions = !impressions || impressions === 0;
  const invalidBudget = !budget || budget === 0;

  const isGuaranteedAndRequiresImpressionOrBudget = isDealTypeGuaranteed ? invalidImpressions && invalidBudget : false;
  const isFloorCeilingSotDisabled = isDisabled || isGuaranteedAndRequiresImpressionOrBudget;

  useEffect(() => {
    if (!isValid()) return;

    dispatch(changeDealCurrentLineData(getValues()));
  }, [sot, floorSot, ceilingSot]);

  return (
    <div data-test-id="sot">
      <label htmlFor="sot" className={labelStyling} data-test-id="sot-header">
        Share of time
      </label>
      <div className="relative" data-test-id="sot-field-info-message">
        <InputNumber
          name="sot"
          value={sot ?? 0}
          onChange={(value: string) => setSot(sotType.SOT, value)}
          placeholder="Add %"
          max={100}
          icon="%"
          isDisabled={hasRange || isInputDisabled}
          dataTestId="sot-input"
        />
        <div className="absolute top-2 -right-8">
          <InfoMessage type={INFO_TYPE.TOOLTIP} direction={TooltipDirection.TOP_LEFT}>
            <div className="w-48">
              {sot
                ? `We’ve found all frames with availability matching your share of time of ${sot}%.`
                : 'To view your Share of time across your frames, please view your frame export.'}
            </div>
          </InfoMessage>
        </div>
      </div>
      <div className="my-4">
        <div className="relative">
          <ToggleSwitch
            label="Add floor to ceiling SOT range"
            isChecked={isToggled}
            onChange={handleToggle}
            isDisabled={isFloorCeilingSotDisabled}
            dataTestId="sot-range-toggle"
          />
          <div className="absolute top-2 -right-8">
            <InfoMessage type={INFO_TYPE.TOOLTIP} direction={TooltipDirection.TOP_LEFT}>
              <div className="w-48">
                {isDealTypeGuaranteed
                  ? 'Impressions or budget must be entered as a goal to use the SOT range'
                  : 'Set floor to ceiling SOT'}
              </div>
            </InfoMessage>
          </div>
        </div>
      </div>
      {isToggled && (!isEqual(floorSot, ceilingSot) || !isFloorCeilingSotDisabled) && (
        <div className="flex">
          <div className="pr-2">
            <label htmlFor="floor-sot" className={labelStyling}>
              Floor share of time
            </label>
            <InputNumber
              name="floor-sot"
              key={randomFloorKey}
              value={floorSot ?? 0}
              onBlur={(value: string) => {
                setSot(sotType.FLOOR, value);
                setRandomFloorKey(Math.random());
              }}
              placeholder="%"
              max={100}
              icon="%"
              maximumFractionDigits={2}
              dataTestId="floor-sot"
              isDisabled={isDisabled}
            />
          </div>
          <div className="pl-2">
            <label htmlFor="ceiling-sot" className={labelStyling}>
              Ceiling share of time
            </label>
            <InputNumber
              name="ceiling-sot"
              key={randomCeilingKey}
              value={ceilingSot ?? 0}
              onBlur={(value: string) => {
                setSot(sotType.CEILING, value);
                setRandomCeilingKey(Math.random());
              }}
              placeholder="%"
              max={100}
              icon="%"
              maximumFractionDigits={2}
              dataTestId="ceiling-sot"
              isDisabled={isDisabled}
            />
          </div>
        </div>
      )}
      <div className="mt-4">
        <InfoMessage cardTheme={CardTheme.PRIMARY}>
          <span className="body-base text-primary-600">
            The default share of time will be allotted to the frames in your deal unless outlined here.
          </span>
        </InfoMessage>
      </div>
    </div>
  );
};

export default ShareOfTime;
/* eslint-enable sonarjs/cognitive-complexity */
